import { useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { AddEditEquipmentDialog } from '../components/inventory/AddEditEquipmentDialog'
import { useAdminEquipment } from '../hooks/useAdminEquipment'
import type { EquipmentDocument, EquipmentStatus } from '@/types'
import type { EquipmentFormValues } from '../types'

const STATUS_BADGE: Record<EquipmentStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  available: { label: 'Available', variant: 'default' },
  in_use: { label: 'In Use', variant: 'secondary' },
  maintenance: { label: 'Maintenance', variant: 'outline' },
  retired: { label: 'Retired', variant: 'secondary' },
}

const STATUS_OPTIONS: { value: EquipmentStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'available', label: 'Available' },
  { value: 'in_use', label: 'In Use' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'retired', label: 'Retired' },
]

export function TechInventoryPage() {
  const { filtered, categories, loading, error, filters, setFilters, addItem, updateItem, deleteItem } =
    useAdminEquipment()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<(EquipmentDocument & { id: string }) | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<(EquipmentDocument & { id: string }) | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  function openAdd() {
    setEditTarget(null)
    setDialogOpen(true)
  }

  function openEdit(item: EquipmentDocument & { id: string }) {
    setEditTarget(item)
    setDialogOpen(true)
  }

  function openDeleteDialog(item: EquipmentDocument & { id: string }) {
    setDeleteTarget(item)
    setDeleteDialogOpen(true)
  }

  async function handleSave(data: EquipmentFormValues) {
    if (editTarget) {
      await updateItem(editTarget.id, data)
    } else {
      await addItem(data)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteItem(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDeleteDialogOpen(false)
    }
  }

  const categoryOptions = [{ value: '', label: 'All Categories' }, ...categories.map((c) => ({ value: c, label: c }))]

  return (
    <AdminListPage
      title="Tech Inventory"
      action={
        <Button onClick={openAdd}>Add Item</Button>
      }
      filters={
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Search equipment…"
            value={filters.searchTerm}
            onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
            className="w-56"
          />
          <Select
            value={filters.status}
            onValueChange={(v) => setFilters((f) => ({ ...f, status: v as EquipmentStatus | '' }))}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value || '__all__'}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.category}
            onValueChange={(v) => setFilters((f) => ({ ...f, category: v === '__all__' ? '' : v }))}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              {categoryOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value || '__all__'}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
      error={error}
      loading={loading}
      dialog={
        <>
          <AddEditEquipmentDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            item={editTarget ?? undefined}
            onSave={handleSave}
          />
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogTrigger className="hidden" />
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Equipment</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? This
                action cannot be undone.
              </p>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteDialogOpen(false)
                    setDeleteTarget(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void handleDelete()}
                  disabled={deletingId !== null}
                >
                  {deletingId !== null ? 'Deleting…' : 'Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Storage Location</TableHead>
            <TableHead>Purchase Cost</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No equipment found. Add items to get started.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item) => {
                const badge = STATUS_BADGE[item.status]
                return (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{item.storageLocation ?? '—'}</TableCell>
                    <TableCell>
                      {item.purchaseCost != null ? `$${item.purchaseCost.toLocaleString()}` : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end">
                        <MultiButton
                          variant="outline"
                          size="sm"
                          items={[
                            { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => openEdit(item) },
                            {
                              id: 'delete',
                              icon: Trash2,
                              label: 'Delete',
                              onClick: () => openDeleteDialog(item),
                              disabled: deletingId === item.id,
                              className: 'text-destructive hover:text-destructive',
                            },
                          ]}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
